import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl-next';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import { ErrorMessage } from '@atlaskit/form';
import { fg } from '@atlaskit/platform-feature-flags';
import Select, { type ValueType } from '@atlaskit/select';
import Input from '@atlaskit/textfield';
import {
	AutoComplete,
	type AutoCompleteItem,
	formatTeamLabel,
	getEscalations,
	getSchedules,
	getTeams,
	getUsers,
} from '@atlassian/eoc-auto-complete';
import { useResponderNames } from '@atlassian/eoc-responder-names';

import { type AvailableNotifyType } from '../../types';
import { isResponderDeleted } from '../../utils';

import messages from './messages';
import {
	ActionButtons,
	Container,
	ErrorMessageWrapper,
	MinuteContainer,
	Row,
	Text,
	WarningMessageWrapper,
} from './styled';
import { type NotifyConditionsOption, type RuleInputProps } from './types';

export const RuleInput: React.FC<RuleInputProps> = ({
	value,
	onChange,
	onCancel,
	notifyTypes,
	warningMessage,
}) => {
	const [responder] = useResponderNames(
		value && !value.inlineResponder ? [{ id: value.notifyId, type: value.type }] : [],
	);
	const { formatMessage } = useIntl();

	const notifyConditions: NotifyConditionsOption[] = [
		{ value: 'if-not-acked', label: formatMessage(messages.notAcknowledged) },
		{ value: 'if-not-closed', label: formatMessage(messages.notClosed) },
	];

	const [notifyCondition, setNotifyCondition] = useState<ValueType<NotifyConditionsOption, false>>(
		notifyConditions[0],
	);
	const [notifyType, setNotifyType] = useState<ValueType<AvailableNotifyType, false>>(
		notifyTypes.find((type) => !type.disabled)!,
	);
	const [time, setTime] = useState('0');
	const [selectedResponder, setSelectedResponder] = useState<ValueType<AutoCompleteItem, false>>();
	const [isSelectedResponderChanged, setIsSelectedResponderChanged] = useState(false);

	useEffect(() => {
		if (value) {
			setNotifyCondition(
				notifyConditions.find((condition) => condition.value === value.notifyCondition)!,
			);
			setNotifyType(
				notifyTypes.find(
					(notifyType) => notifyType?.value === value.notifyType && notifyType?.type === value.type,
				)!,
			);
			setTime(value.time.toString());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (value) {
			if (value.inlineResponder) {
				setSelectedResponder(value.inlineResponder);

				setIsSelectedResponderChanged(true);

				return;
			}

			if (isResponderDeleted(responder)) {
				setSelectedResponder(null);
				return;
			}

			if (responder && !responder.isLoading && !responder.isError) {
				setSelectedResponder({
					label: responder.name,
					value: responder.id,
					type: responder.type,
					...(fg('display_eoc_managed_teams_with_verified_marks')
						? {
								verified: responder.verified,
								avatarUrl: responder.avatarUrl,
							}
						: {}),
				});

				setIsSelectedResponderChanged(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [responder]);

	const handleNotifyConditionChange = (
		selectedOption: ValueType<NotifyConditionsOption, false>,
	) => {
		setNotifyCondition(selectedOption);
	};

	const handleTimeChange = (value: string) => {
		if (value.startsWith('0') && value.length > 1) {
			const newValue = value.replace(/^0+/, '');
			setTime(newValue);
			return;
		}

		setTime(value);
	};

	const handleNotifyTypeChange = (selectedOption: ValueType<AvailableNotifyType, false>) => {
		setNotifyType(selectedOption);

		const hasNewType = selectedOption!.type !== notifyType?.type;

		if (hasNewType) {
			setIsSelectedResponderChanged(false);
			setSelectedResponder(null);
		}
	};

	const handleResponderChange = (selectedOption: ValueType<AutoCompleteItem, false>) => {
		setIsSelectedResponderChanged(true);
		setSelectedResponder(selectedOption);
	};

	const validateTimeInput = () => {
		if (time) {
			if (!/^(|-?\d+)$/.test(time)) {
				return formatMessage(messages.validNumber);
			}
		}

		const timeInt = parseInt(time, 10);

		if (timeInt !== 0 && !time) {
			return formatMessage(messages.fieldRequired);
		}

		if (timeInt > 1440) {
			return formatMessage(messages.timeMaxLimit);
		}

		if (timeInt < 0) {
			return formatMessage(messages.timeMinLimit);
		}

		return '';
	};

	const validateResponderInput = (fromSave?: boolean) => {
		if (fromSave && !selectedResponder) {
			setIsSelectedResponderChanged(true);

			return formatMessage(messages.fieldRequired);
		}

		if (!selectedResponder && isSelectedResponderChanged && isResponderDeleted(responder)) {
			return formatMessage(messages.deletedFieldRequired);
		}

		if (isSelectedResponderChanged && !selectedResponder) {
			return formatMessage(messages.fieldRequired);
		}

		return '';
	};

	const handleSave = () => {
		const isTimeInvalid = validateTimeInput();
		const isResponderInvalid = validateResponderInput(true);

		if (!!isTimeInvalid || !!isResponderInvalid) {
			return;
		}

		if (!isTimeInvalid || !isResponderInvalid) {
			const values = {
				notifyCondition: notifyCondition!.value,
				type: notifyType!.type,
				notifyType: notifyType!.value,
				time: parseInt(time, 10),
				notifyId: selectedResponder!.value,
				inlineResponder: selectedResponder,
			};

			if (onChange) {
				onChange(values);
			}
		}
	};

	const getAutoCompleteEndpoint = () => {
		if (notifyType?.type === 'escalation') {
			return [getEscalations];
		}

		if (notifyType?.type === 'schedule') {
			return [getSchedules];
		}

		if (notifyType?.type === 'team') {
			return [getTeams];
		}

		return [getUsers];
	};

	return (
		<div>
			<Container>
				<Row>
					<Text>{formatMessage(messages.ifTheAlertIs)}</Text>
					<Select
						options={notifyConditions}
						value={notifyCondition}
						onChange={handleNotifyConditionChange}
						menuPosition="fixed"
						label={formatMessage(messages.notifyConditionLabel)}
					/>
					<MinuteContainer>
						<Input
							name="time"
							aria-label="time"
							value={time}
							onKeyPress={(e) => {
								if (e.key === '.' || e.key === ',' || e.key === 'e') {
									e.preventDefault();
								}
							}}
							onChange={(e) => handleTimeChange(e.currentTarget.value)}
							type="number"
							min="0"
							max="1440"
							step="1"
							isInvalid={!!validateTimeInput()}
						/>
						<Text>{formatMessage(messages.minutesAfterCreation)}</Text>
						{validateTimeInput() && (
							<ErrorMessageWrapper>
								<ErrorMessage>{validateTimeInput()}</ErrorMessage>
							</ErrorMessageWrapper>
						)}
					</MinuteContainer>
				</Row>
				<Row>
					<Text>{formatMessage(messages.then)}</Text>
					<Select
						options={notifyTypes}
						value={notifyType}
						isOptionSelected={(option, [selectedOption]) =>
							option.value === selectedOption.value && option.type === selectedOption.type
						}
						onChange={handleNotifyTypeChange}
						isMulti={false}
						isOptionDisabled={(option) => option.disabled}
						menuPosition="fixed"
						label={formatMessage(messages.notifyActionLabel)}
					/>
					{/* we should get this data from responder names or auto complete */}
					<div>
						<AutoComplete
							label={formatMessage(messages.searchFor, {
								type: notifyType!.type,
							})}
							endpoints={getAutoCompleteEndpoint()}
							placeholder={
								isResponderDeleted(responder)
									? formatMessage(messages.deletedResponder)
									: formatMessage(messages.searchFor, {
											type: notifyType!.type,
										})
							}
							onChange={handleResponderChange}
							isInvalid={!!validateResponderInput()}
							isLoading={responder?.isLoading}
							value={selectedResponder}
							menuPosition="fixed"
							formatOptionLabel={
								fg('display_eoc_managed_teams_with_verified_marks') ? formatTeamLabel : undefined
							}
						/>
						{validateResponderInput() && (
							<ErrorMessageWrapper>
								<ErrorMessage>{validateResponderInput()}</ErrorMessage>
							</ErrorMessageWrapper>
						)}
					</div>
				</Row>
			</Container>
			<ActionButtons>
				{warningMessage && <WarningMessageWrapper>{warningMessage}</WarningMessageWrapper>}
				<ButtonGroup>
					<Button appearance="subtle" onClick={onCancel}>
						{formatMessage(messages.cancel)}
					</Button>
					{/* todo: check this on jira for if something overrides it. */}
					<Button onClick={handleSave}>{formatMessage(messages.save)}</Button>
				</ButtonGroup>
			</ActionButtons>
		</div>
	);
};
